import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../config/hooks";
import { getInterestForms, deleteInterestForm, editInterestForm } from "../../actions/interests";
import { InterestForm, SCHOOL_OPTIONS } from "../../@types";

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useMediaQuery,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const SCROLL_SX = {
  "&::-webkit-scrollbar": { width: 10, height: 10 },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#4b5563", borderRadius: 999 },
  "&::-webkit-scrollbar-track": { backgroundColor: "#111827" },
  scrollbarColor: "#4b5563 #111827",
};

const AdminInterestsTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, status, error } = useAppSelector((s) => s.interests);

  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<InterestForm | null>(null);

  const isMobile = useMediaQuery("(max-width: 767px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);

  useEffect(() => {
    dispatch(getInterestForms());
  }, [dispatch]);

  const allSchools = useMemo(() => {
    const present = Array.from(new Set(items.map((i) => i.school).filter(Boolean)));
    const merged = [...SCHOOL_OPTIONS];
    present.forEach((s) => {
      if (!merged.includes(s as any)) merged.push(s as any);
    });
    return merged;
  }, [items]);

  const tabs = useMemo(() => ["All", ...allSchools], [allSchools]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    items.forEach((i) => {
      const k = i.school || "Unknown";
      map.set(k, (map.get(k) || 0) + 1);
    });
    return map;
  }, [items]);

  const filtered = useMemo(() => {
    const base = tab === 0 ? items : items.filter((i) => i.school === tabs[tab]);
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((i) =>
      [i.full_name, i.email_address, i.phone_number, i.major, i.school]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q))
    );
  }, [items, tab, tabs, query]);

  const onEditOpen = (row: InterestForm) => {
    setEditForm(row);
    setEditOpen(true);
  };
  const onEditClose = () => {
    setEditOpen(false);
    setEditForm(null);
  };
  const onEditChange =
    (name: keyof InterestForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
      editForm && setEditForm({ ...editForm, [name]: e.target.value });
  const onEditSubmit = async () => {
    if (!editForm) return;
    try {
      await dispatch(editInterestForm(editForm)).unwrap();
      onEditClose();
    } catch (e) {
      console.error(e);
    }
  };
  const onDelete = async (id: number) => {
    try {
      await dispatch(deleteInterestForm(id)).unwrap();
      setConfirmId(null);
    } catch (e) {
      console.error(e);
    }
  };

  if (status === "pending" && items.length === 0) {
    return <div className="p-6 text-gray-300">Loading interests…</div>;
  }
  if (status === "failed") {
    const msg = (error && (error as any).detail) || (error as any)?.message || "Failed to load interests.";
    return <div className="p-6 text-red-400">Error: {msg}</div>;
  }

  const th = {
    fontWeight: 700,
    background: "#0f172a",
    color: "#ffffff",
    borderBottom: "1px solid #1f2937",
    position: "sticky" as const,
    top: 0,
    zIndex: 1,
  };
  const td = {
    color: "#ffffff",
    borderBottom: "1px solid #1f2937",
    maxWidth: 260,
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const DrawerContent = (
    <div className="h-full bg-[#0b0b0b] text-gray-100 w-[85vw] max-w-xs">
      <div className="p-4 border-b border-gray-700">
        <Typography variant="subtitle1" sx={{ color: "#ffffff", fontWeight: 600 }}>
          Filter by School
        </Typography>
        <Typography variant="body2" sx={{ color: "#9ca3af" }}>
          Choose a school to filter the list.
        </Typography>
      </div>
      <List dense disablePadding>
        <ListItemButton
          selected={tab === 0}
          onClick={() => {
            setTab(0);
            setDrawerOpen(false);
          }}
          sx={{
            color: "#ffffff",
            "&.Mui-selected": { backgroundColor: "#111827", borderLeft: "3px solid #6366f1" },
          }}
        >
          <ListItemText
            primary={
              <div className="flex items-center justify-between gap-2 w-full">
                <span>All</span>
                <span className="shrink-0 rounded-full bg-indigo-500/10 text-indigo-200 border border-indigo-500/40 px-2 py-[2px] text-[11px]">
                  {items.length}
                </span>
              </div>
            }
          />
        </ListItemButton>
        {allSchools.map((school, idx) => {
          const i = idx + 1;
          const count = counts.get(school) || 0;
          return (
            <ListItemButton
              key={school}
              selected={tab === i}
              onClick={() => {
                setTab(i);
                setDrawerOpen(false);
              }}
              sx={{
                color: "#ffffff",
                "&.Mui-selected": { backgroundColor: "#111827", borderLeft: "3px solid #6366f1" },
              }}
            >
              <ListItemText
                primary={
                  <div className="flex items-center justify-between gap-2 w-full">
                    <span>{school}</span>
                    <span className="shrink-0 rounded-full bg-indigo-500/10 text-indigo-200 border border-indigo-500/40 px-2 py-[2px] text-[11px]">
                      {count}
                    </span>
                  </div>
                }
              />
            </ListItemButton>
          );
        })}
      </List>
      <div className="p-3 border-t border-gray-700">
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            setTab(0);
            setDrawerOpen(false);
          }}
          sx={{ borderColor: "#4b5563", color: "#ffffff" }}
        >
          Reset to All
        </Button>
      </div>
    </div>
  );

  return (
    <Box className="w-full overflow-x-hidden" sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 1.25, md: 2 },
          bgcolor: "#0a0a0a",
          border: "1px solid #374151",
          position: "sticky",
          top: 0,
          zIndex: 3,
        }}
      >
        <Toolbar disableGutters sx={{ gap: 2 }}>
          {isMobile && (
            <Button
              onClick={toggleDrawer(true)}
              size="small"
              variant="outlined"
              startIcon={<MenuIcon htmlColor="#ffffff" />}
              sx={{
                color: "#ffffff",
                borderColor: "#4b5563",
                bgcolor: "#111827",
                textTransform: "none",
                "&:hover": { borderColor: "#6b7280", bgcolor: "#1f2937" },
              }}
            >
              Filters
            </Button>
          )}
          <Typography variant="h6" sx={{ color: "#ffffff", mr: "auto" }}>
            Interest Submissions
          </Typography>
          <TextField
            size="small"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{
              "& .MuiInputBase-root": { color: "#ffffff" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#374151" },
            }}
            className="w-full max-w-sm"
          />
        </Toolbar>

        {!isMobile && (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mt: 1,
              "& .MuiTab-root": { color: "#9ca3af" },
              "& .Mui-selected": { color: "#ffffff !important" },
              "& .MuiTabs-indicator": { backgroundColor: "#6366f1" },
              "& .MuiTabScrollButton-root": { color: "#ffffff" }, // <-- arrows white
              "& .MuiTabScrollButton-root.Mui-disabled": { opacity: 0.3 },
            }}
          >
            {tabs.map((label, i) => {
              const count = i === 0 ? items.length : counts.get(label as string) || 0;
              return (
                <Tab
                  key={label}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{label}</span>
                      <Chip size="small" label={count} />
                    </Stack>
                  }
                />
              );
            })}
          </Tabs>
        )}
      </Paper>

      {/* Drawer for mobile filters */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)} PaperProps={{ sx: { bgcolor: "#0b0b0b" } }}>
        {DrawerContent}
      </Drawer>

      {/* Mobile: cards */}
      {isMobile && (
        <div className="space-y-3">
          {filtered.map((row) => (
            <article key={row.id} className="rounded-xl border border-gray-700 bg-[#0b0b0b] px-3 py-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold truncate">{row.full_name}</h3>
                  <div className="text-gray-300 text-sm break-words">{row.email_address}</div>
                  <div className="text-gray-300 text-sm">{row.phone_number}</div>
                  <div className="text-gray-300 text-sm">{row.major}</div>
                  <div className="text-indigo-200 text-xs">{row.school}</div>
                </div>
                <div className="flex gap-1">
                  <IconButton onClick={() => onEditOpen(row)} size="small" sx={{ color: "#ffffff" }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => setConfirmId(row.id)} size="small" sx={{ color: "#ffffff" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Desktop: table */}
      {!isMobile && (
        <TableContainer component={Paper} sx={{ bgcolor: "#0b0b0b", border: "1px solid #1f2937", ...SCROLL_SX }}>
          <Table stickyHeader className="w-full">
            <TableHead>
              <TableRow>
                <TableCell sx={th}>Name</TableCell>
                <TableCell sx={th}>Email</TableCell>
                <TableCell sx={th}>Phone</TableCell>
                <TableCell sx={th}>Major</TableCell>
                <TableCell sx={th}>School</TableCell>
                <TableCell sx={th} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={td}>{row.full_name}</TableCell>
                  <TableCell sx={td}>{row.email_address}</TableCell>
                  <TableCell sx={td}>{row.phone_number}</TableCell>
                  <TableCell sx={td}>{row.major}</TableCell>
                  <TableCell sx={td}>{row.school}</TableCell>
                  <TableCell sx={{ color: "#ffffff" }} align="center">
                    <IconButton onClick={() => onEditOpen(row)} size="small" sx={{ color: "#ffffff" }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => setConfirmId(row.id)} size="small" sx={{ color: "#ffffff" }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete dialog */}
      {confirmId !== null && (
        <Dialog open onClose={() => setConfirmId(null)}>
          <DialogTitle>Delete submission?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setConfirmId(null)}>Cancel</Button>
            <Button color="error" onClick={() => onDelete(confirmId!)}>Delete</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={onEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Interest</DialogTitle>
        <DialogContent>
          {editForm && (
            <Stack spacing={2}>
              <TextField label="Full Name" value={editForm.full_name} onChange={onEditChange("full_name")} fullWidth />
              <TextField label="Email" value={editForm.email_address} onChange={onEditChange("email_address")} fullWidth type="email" />
              <TextField label="Phone" value={editForm.phone_number} onChange={onEditChange("phone_number")} fullWidth />
              <TextField label="Major" value={editForm.major} onChange={onEditChange("major")} fullWidth />
              <TextField select label="School" value={editForm.school} onChange={(e) => setEditForm({ ...editForm, school: e.target.value })} fullWidth>
                {SCHOOL_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onEditClose}>Cancel</Button>
          <Button onClick={onEditSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminInterestsTable;
